import React from 'react';
import { Box, Typography, Grid, Button, Paper } from '@mui/material';
import SportsSoccerOutlinedIcon from '@mui/icons-material/SportsSoccerOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import TvOutlinedIcon from '@mui/icons-material/TvOutlined';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import { format, isValid, parse, parseISO } from 'date-fns';

interface EventDetailViewProps {
	event: {
		id: number;
		title: string;
		date: string;
		time: string;
		timezone: string;
		sport?: string | null;
		teams?: string | null;
		why_hot: string;
		broadcast?: string | null;
		city: string;
		country: string;
		zipcode: string;
	};
	onClose: () => void;
	eventType: 'local-event' | 'hot-game';
}

const BRAND = 'rgb(68, 99, 136)';
const BRAND_TINT = 'rgba(68, 99, 136, 0.06)';

// parseISO treats 'YYYY-MM-DD' as a local date, avoiding the UTC off-by-one of new Date()
const formatDate = (date: string): string => {
	if (!date) return '';
	const parsed = parseISO(date);
	return isValid(parsed) ? format(parsed, 'EEEE, MMMM d, yyyy') : date;
};

const formatTime = (time: string): string => {
	if (!time) return '';
	const pattern = time.length > 5 ? 'HH:mm:ss' : 'HH:mm';
	const parsed = parse(time, pattern, new Date());
	return isValid(parsed) ? format(parsed, 'h:mm a') : time;
};

interface InfoTileProps {
	icon: React.ReactNode;
	label: string;
	value: string;
	secondary?: string;
}

const InfoTile: React.FC<InfoTileProps> = ({ icon, label, value, secondary }) => (
	<Box
		sx={{
			display: 'flex',
			alignItems: 'flex-start',
			gap: 1.5,
			p: 2.5,
			height: '100%',
			borderRadius: 2,
			backgroundColor: BRAND_TINT,
		}}
	>
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: 40,
				height: 40,
				flexShrink: 0,
				borderRadius: '50%',
				backgroundColor: '#fff',
				color: BRAND,
			}}
		>
			{icon}
		</Box>
		<Box minWidth={0}>
			<Typography
				variant="caption"
				sx={{
					display: 'block',
					textTransform: 'uppercase',
					letterSpacing: 0.8,
					fontWeight: 600,
					color: 'text.secondary',
				}}
			>
				{label}
			</Typography>
			<Typography variant="body1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
				{value}
			</Typography>
			{secondary && (
				<Typography variant="body2" color="text.secondary">
					{secondary}
				</Typography>
			)}
		</Box>
	</Box>
);

const EventDetailView: React.FC<EventDetailViewProps> = ({ event, onClose, eventType }) => {
	const isLocalEvent = eventType === 'local-event';
	const headerTitle = isLocalEvent ? 'Local Event Details' : 'Hot Game Details';
	const sport = event.sport ? event.sport.charAt(0).toUpperCase() + event.sport.slice(1) : '';

	const formattedDate = formatDate(event.date);
	const formattedTime = formatTime(event.time);
	const cityCountry = [event.city, event.country].filter(Boolean).join(', ');
	const location = [cityCountry, event.zipcode].filter(Boolean).join(' ');

	return (
		<Paper sx={{ minHeight: '100vh', py: 3, borderRadius: 3, boxShadow: 2 }}>
			<Box maxWidth="1100px" mx="auto" px={3}>
				{/* Header */}
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
					<Button
						onClick={onClose}
						sx={{
							backgroundColor: BRAND,
							color: '#fff',
							textTransform: 'none',
							fontWeight: 500,
							px: 3,
							py: 0.75,
							'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
						}}
					>
						Back
					</Button>
					<Typography variant="h5" sx={{ fontWeight: 600 }}>
						{headerTitle}
					</Typography>
					<Box sx={{ width: '120px' }} />
				</Box>

				<Box sx={{ p: { xs: 1, sm: 4 } }}>
					{/* Hero */}
					<Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
						{event.title || 'Untitled event'}
					</Typography>
					{event.teams && (
						<Typography variant="h6" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
							{event.teams}
						</Typography>
					)}

					{/* Key facts */}
					<Grid container spacing={2} sx={{ mt: 2 }}>
						{sport && (
							<Grid item xs={12} sm={6} md={4}>
								<InfoTile
									icon={<SportsSoccerOutlinedIcon fontSize="small" />}
									label="Sport"
									value={sport}
								/>
							</Grid>
						)}
						{formattedDate && (
							<Grid item xs={12} sm={6} md={4}>
								<InfoTile
									icon={<CalendarTodayOutlinedIcon fontSize="small" />}
									label="Date"
									value={formattedDate}
								/>
							</Grid>
						)}
						{formattedTime && (
							<Grid item xs={12} sm={6} md={4}>
								<InfoTile
									icon={<AccessTimeIcon fontSize="small" />}
									label="Time"
									value={formattedTime}
									secondary={event.timezone}
								/>
							</Grid>
						)}
						{location && (
							<Grid item xs={12} sm={6} md={4}>
								<InfoTile
									icon={<PlaceOutlinedIcon fontSize="small" />}
									label="Location"
									value={location}
								/>
							</Grid>
						)}
						{event.broadcast && (
							<Grid item xs={12} sm={6} md={4}>
								<InfoTile
									icon={<TvOutlinedIcon fontSize="small" />}
									label="Broadcast"
									value={event.broadcast}
								/>
							</Grid>
						)}
					</Grid>

					{/* Why it's hot */}
					{event.why_hot && (
						<Box
							sx={{
								mt: 3,
								p: 3,
								borderRadius: 2,
								borderLeft: `4px solid ${BRAND}`,
								backgroundColor: BRAND_TINT,
							}}
						>
							<Box display="flex" alignItems="center" gap={1} mb={1}>
								<WhatshotIcon sx={{ color: '#e8590c' }} fontSize="small" />
								<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
									Why it&apos;s hot
								</Typography>
							</Box>
							<Typography variant="body1" sx={{ lineHeight: 1.7 }}>
								{event.why_hot}
							</Typography>
						</Box>
					)}
				</Box>
			</Box>
		</Paper>
	);
};

export default EventDetailView;
